import React, { Activity } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import Avatar from '@mui/material/Avatar';
import Container from '@mui/material/Container';
import ErrorBoundary from 'sensortower-components/src/base-components/ErrorBoundary';
import { styled } from 'sensortower-components/src/utils/styled';
import Media, { MediaProps } from 'sensortower-components/src/base-components/Media';
import { RichText } from 'sensortower-components/src/base-components/Text';
import Header, { HeaderProps } from 'sensortower-components/src/base-components/Header';

import Footer, { FooterProps } from '../../components/Footer/Footer';
import SearchBox from '../../components/SearchBox/SearchBox';
import CommonResource, { CommonResourceProps } from '../../components/CommonResource/CommonResource';
import Link from '../../components/Link';
import Text from '../../components/Text';
import AnnouncementBanner from '../../components/AnnouncementBanner';
import { sidekick } from '../../utils/sidekick';
import { ensureAbsoluteUrl, getSiteUrl } from '../../utils/path';
import { organization, toJsonLd } from '../../utils/structuredData';
import type { Locale } from '../../types';

interface Author {
  name?: string;
  jobTitle?: string;
  image?: MediaProps;
}

interface Tag {
  title?: string;
  slug?: string;
  seo?: JSON;
}

export interface Category extends Tag {}

export interface BlogProps {
  sidekickLookup: any;
  title?: string;
  slug?: string;
  summary?: string;
  featuredMedia?: MediaProps;
  category?: Category[];
  author?: Author;
  pubDate?: string;
  /** ISO 8601 publish date from Contentful; `pubDate` is the display string. */
  pubDateIso?: string;
  body?: RichText;
  tags?: Tag[];
  commonText?: CommonResourceProps[];
  header?: HeaderProps;
  footer?: FooterProps;
}

export const Blog = ({
  sidekickLookup,
  title,
  slug,
  summary,
  featuredMedia,
  category,
  author,
  pubDate,
  pubDateIso,
  body,
  tags,
  commonText,
  header,
  footer,
}: BlogProps) => {
  // Storybook and Cypress mounts render this page without a router.
  const locale = (useRouter()?.locale ?? 'en-US') as Locale;
  const keywords = tags?.map((tag) => tag.title).filter(Boolean) ?? [];
  // Values missing in Contentful arrive as `null` over GraphQL; `undefined` drops them
  // from the serialised JSON instead of publishing empty properties.
  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: title || undefined,
    description: summary || undefined,
    image: featuredMedia?.file?.url ? ensureAbsoluteUrl(featuredMedia.file.url) : undefined,
    keywords: keywords.length ? keywords.join(', ') : undefined,
    datePublished: pubDateIso || undefined,
    url: slug ? getSiteUrl(`/blog/${slug}`, locale) : undefined,
    author: author?.name ? { '@type': 'Person', name: author.name } : undefined,
    publisher: organization,
  };

  return (
    <>
      <Head>
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: toJsonLd(schemaData) }} />
      </Head>
      {header ? <Header {...header} /> : null}

      <ErrorBoundary>
        <Root {...sidekick(sidekickLookup)}>
          <AnnouncementBanner />
          <SearchBox />
          <main id="page-content">
            <BlogHeader
              sidekickLookup={sidekickLookup}
              author={author}
              category={category}
              pubDate={pubDate}
              title={title}
              summary={summary}
              featuredMedia={featuredMedia}
            />
            {/* Each Activity boundary hydrates as its own task, as in PageGeneral. The header
                and blog header hydrate with the root. */}
            <Activity>
              <BlogBody author={author} pubDate={pubDate} body={body} tags={tags} sidekickLookup={sidekickLookup} />
            </Activity>
            {commonText && (
              <Activity>
                <BlogBodyFooter maxWidth="xl">
                  {commonText?.map((text) => (
                    <CommonResource {...text} key={text.id} />
                  ))}
                </BlogBodyFooter>
              </Activity>
            )}
            <Activity>
              <AuthorBox author={author} pubDate={pubDate} tags={tags} sidekickLookup={sidekickLookup} />
            </Activity>
          </main>
        </Root>
      </ErrorBoundary>

      {footer ? (
        <Activity>
          <Footer {...footer} />
        </Activity>
      ) : null}
    </>
  );
};

const BlogHeader = ({ author, category, pubDate, title, summary, featuredMedia, sidekickLookup }: BlogProps) => (
  <BlogHeaderRoot
    {...sidekick(sidekickLookup)}
    sx={{
      width: '100%',
      maxWidth: 959,
      margin: '0 auto',
      paddingTop: [4, 4, 6],
      paddingBottom: 4,
    }}
  >
    <Box
      sx={{
        py: 2,
      }}
    >
      <Typography variant="h5" align="center" sx={{ color: 'secondary.contrastText' }}>
        {category?.map((cat) => (
          <span key={cat.title}> {cat.title + ' · '} </span>
        ))}
        {author?.name} · {pubDate}
      </Typography>
    </Box>

    {title ? (
      <Typography variant="h1" align="center" sx={{ color: 'secondary.contrastText' }}>
        {title}
      </Typography>
    ) : null}

    {summary ? (
      <Box
        sx={{
          py: 3,
          px: { md: 4 },
        }}
      >
        {/* The summary is a paragraph styled as h4; an <h4> right after the <h1> skips heading levels. */}
        <Typography variant="h4" component="p" align="center">
          {summary}
        </Typography>
      </Box>
    ) : null}

    {featuredMedia ? <BackgroundMedia {...featuredMedia} sidekickLookup={sidekickLookup?.featuredMedia} /> : null}
  </BlogHeaderRoot>
);

const BlogBody = ({ sidekickLookup, body }: BlogProps) => (
  <BlogBodyRoot
    sx={{
      width: '100%',
      maxWidth: 959,
      margin: '0 auto',
      paddingTop: 2,
      paddingBottom: 2,
    }}
    {...sidekick(sidekickLookup)}
  >
    {body ? <Text variant="blog" sidekickLookup={sidekickLookup?.body} body={body} /> : null}

    <Divider sx={{ mx: [0], my: [1, 2] }} />
  </BlogBodyRoot>
);

const AuthorBox = ({ author, pubDate, sidekickLookup, tags }: BlogProps) => (
  <AuthorBoxRoot
    sx={{
      width: '100%',
      maxWidth: 959,
      margin: '0 auto 16px',
      paddingTop: 2,
      paddingBottom: 2,
    }}
    {...sidekick(sidekickLookup)}
  >
    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
      <Avatar
        sx={{ width: 150, height: 150, marginBottom: [0, 3, 0], marginRight: [3, 5] }}
        alt={author?.name}
        src={author?.image?.file?.url ? `${author?.image?.file?.url}?fm=webp&q=60&w=600` : undefined}
      />
    </Box>

    <Box>
      {author?.name ? (
        <Box sx={{ paddingTop: [5, 0] }}>
          <Typography variant="body1" align="left">
            <strong>Written by: </strong> {[author.name, author.jobTitle].filter(Boolean).join(', ')}
          </Typography>
        </Box>
      ) : null}

      {pubDate ? (
        <Typography variant="body1" align="left">
          <strong>Date: </strong> {pubDate}
        </Typography>
      ) : null}

      {tags ? (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            pt: 2,
            justifyContent: ['center', 'flex-start'],
            margin: (theme) => theme.spacing(0, -1),
          }}
        >
          {tags.map((tag, index) => (
            <Box
              // Both `slug` and `title` are optional on a Tag, so neither is a dependable key on
              // its own; the index keeps it defined and unique for tags that share a title.
              key={`${tag.slug ?? tag.title ?? 'tag'}-${index}`}
              sx={{
                display: 'flex',
                margin: (theme) => theme.spacing(0.5, 1),
                borderRadius: '50px',
                border: (theme) => `2px solid ${theme.palette.secondary.contrastText}`,
                color: 'secondary.contrastText',
                fontSize: 14,
                fontWeight: 600,
                letterSpacing: '2px',
                '&:hover': {
                  backgroundColor: (theme) => theme.palette.primary.main,
                  transition: 'background-color 0.18s linear',
                  '& a': {
                    color: (theme) => theme.palette.primary.contrastText,
                  },
                },
                '& a': {
                  padding: (theme) => theme.spacing(1, 2),
                },
              }}
            >
              <Link
                href={tag.slug}
                sx={{
                  textDecoration: 'none',
                  fontSize: '15px',
                }}
              >
                {tag.title}
              </Link>
            </Box>
          ))}
        </Box>
      ) : null}
    </Box>
  </AuthorBoxRoot>
);

export default Blog;

const Root = styled(Box, {
  name: 'Blog',
  slot: 'root',
})<{ variant?: string }>(() => ({}));

const BlogHeaderRoot = styled('header', {
  name: 'Blog',
  slot: 'blogHeader',
})<{ variant?: string }>(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  [theme.breakpoints.down('lg')]: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
}));

const BackgroundMedia = styled(Media, {
  name: 'Blog',
  slot: 'BackgroundMedia',
  overridesResolver: (_, styles) => [styles.backgroundImage],
})(() => ({
  display: 'block',
  width: '100%',
  height: '100%',
  maxHeight: '555px',
  objectFit: 'cover',
}));

const BlogBodyRoot = styled(Grid, {
  name: 'Blog',
  slot: 'blogBodyRoot',
})<{ variant?: string }>(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  margin: 'auto',
  [theme.breakpoints.down('lg')]: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
  '& h2': {
    marginBottom: theme.spacing(3),
  },
  '& .box-with-embed': {
    paddingBottom: theme.spacing(2),
  },
  '& p, & .box-with-embed': {
    marginBottom: theme.spacing(2),
    fontSize: 20,
    fontWeight: 400,
    lineHeight: 1.3,
    letterSpacing: '-0.5px',
    [theme.breakpoints.up('md')]: {
      marginBottom: theme.spacing(3),
      fontSize: 25,
    },
    '& b': {
      fontWeight: 700,
    },
  },
  '& hr': {
    border: `0 solid ${theme.palette.primary.main}`,
    borderBottomWidth: '1px',
  },
  '& img': {
    width: '100%',
    height: 'auto',
    marginBottom: theme.spacing(2),
  },
  /* Centers 'Download Report' button */
  '& [class*="Text-root"] > [class*="Box-content"]:last-of-type': {
    justifyContent: 'center',
    width: '100%',
  },
  /*
    Centers 'Download Report' button
    TODO: The previous solution doesn't seem to work, need to find a better solution
  */
  '& [class*="Text-root"] > [data-testid="Text-embedded-entry-block"]:last-of-type': {
    textAlign: 'center',
  },
}));

const AuthorBoxRoot = styled(Box, {
  name: 'BlogBody',
  slot: 'authorBoxRoot',
})<{ variant?: string }>(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  margin: 'auto',
  paddingBottom: theme.spacing(8),
  [theme.breakpoints.down('lg')]: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
  },
  '& p': {
    fontSize: 20,
    fontWeight: 400,
    lineHeight: 1.3,
    letterSpacing: '-0.5px',
    [theme.breakpoints.up('md')]: {
      fontSize: 25,
    },
    '& b': {
      fontWeight: 700,
    },
  },
}));

const BlogBodyFooter = styled(Container, {
  name: 'BlogBody',
  slot: 'blogBodyFooter',
})<{ variant?: string }>(() => ({
  textAlign: 'center',
}));
